import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign,
  Receipt,
  FileCheck,
  TrendingUp,
  Target,
  AlertTriangle,
  Globe,
  ArrowRight,
  CreditCard,
  Calculator, // Billing Dashboard icon
  Search
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const FinanceLanding = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const financeCards = [
    {
      id: 'asset-finance',
      title: 'Asset Finance',
      description: 'Capitalization, depreciation & valuation management',
      icon: <DollarSign size={24} />,
      path: '/finance/assets',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Capitalization', 'Depreciation', 'Valuation']
    },
    {
      id: 'contract-finance',
      title: 'Contract Finance',
      description: 'Payment terms, AP/AR & revenue recognition',
      icon: <Receipt size={24} />,
      path: '/finance/contracts',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Payment terms', 'AP/AR', 'Revenue recognition']
    },
    // New Payment Management Card
    {
      id: 'payment-management',
      title: 'Payment Management',
      description: 'Track milestones, recurring payments & invoice status',
      icon: <CreditCard size={24} />,
      path: '/finance/payments',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Milestone tracking', 'Recurring payments', 'Status updates']
    },
    {
      id: 'billing-dashboard',
      title: 'Billing Dashboard',
      description: 'Comprehensive billing and payment tracking',
      icon: <Calculator size={24} />,
      path: '/finance/billing-dashboard',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Payment terms', 'AP/AR', 'Revenue recognition']
    },
    {
      id: 'invoice-validation',
      title: 'Invoice Validation',
      description: 'AI-OCR automated validation & processing',
      icon: <FileCheck size={24} />,
      path: '/finance/invoices',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['AI-OCR', 'Automated validation', 'Processing']
    },
    {
      id: 'cash-flow-forecasting',
      title: 'Cash Flow Forecasting',
      description: 'AI predictive cash flow analysis & planning',
      icon: <TrendingUp size={24} />,
      path: '/finance/cashflow',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['AI predictions', 'Cash flow analysis', 'Planning']
    },
    {
      id: 'spend-optimization',
      title: 'Spend Optimization',
      description: 'AI spend analysis & cost optimization recommendations',
      icon: <Target size={24} />,
      path: '/finance/optimization',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Spend analysis', 'Cost optimization', 'AI recommendations']
    },
    {
      id: 'risk-exposure',
      title: 'Risk & Exposure',
      description: 'Financial risk & exposure analysis',
      icon: <AlertTriangle size={24} />,
      path: '/finance/risk',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Risk analysis', 'Exposure tracking', 'Compliance']
    },
    {
      id: 'multi-currency',
      title: 'Multi-Currency',
      description: 'Global contracts & tax compliance management',
      icon: <Globe size={24} />,
      path: '/finance/currency',
      iconBg: '#D6EFFF',
      iconLine: 'var(--gold-primary)',
      features: ['Global contracts', 'Tax compliance', 'Multi-currency']
    }
  ];

  const handleCardClick = (path) => {
    navigate(path);
  };

  const filteredCards = financeCards.filter((card) => {
    const query = searchQuery.toLowerCase();
    return (
      card.title.toLowerCase().includes(query) ||
      card.description.toLowerCase().includes(query) ||
      card.features.some((f) => f.toLowerCase().includes(query))
    );
  });

  return (
    <>
      <style>{`
        .finance-search-input {
          width: 300px;
        }
        @media (max-width: 768px) {
          .finance-search-input {
            width: 100%;
          }
        }
      `}</style>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb 
        rightContent={
          <div style={{ position: 'relative' }} className="finance-search-input">
            <Search 
              size={18} 
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#6B7280'
              }} 
            />
            <input
              type="text"
              placeholder="Search finance modules..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                fontSize: '14px',
                fontFamily: 'var(--font-family)',
                backgroundColor: '#FFFFFF',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--gold-primary)'}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
            />
          </div>
        }
      />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Finance Management"
        description="Comprehensive financial management and optimization system"
      />
      <div className="page-container">
        {/* Finance Management Cards */}
        <div
          className="grid-modern-3"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%'
          }}
        >
          {filteredCards.length > 0 ? (
            filteredCards.map((card) => (
            <div
              key={card.id}
              className="asset-landing-card"
              onClick={() => handleCardClick(card.path)}
            >
              {/* Top Accent Line */}
              <div
                style={{
                  height: '4px',
                  background: `var(--gold-primary)`,
                  width: '100%'
                }}
              />

              {/* Card Content */}
              <div style={{ padding: '24px' }}>
                {/* Icon and Title */}
                <div className="d-flex align-items-center mb-3">
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: card.iconBg || '#FFF9E6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '16px'
                    }}
                  >
                    <div style={{ color: card.iconLine || 'var(--gold-primary)' }} className="asset-card-icon">
                      {card.icon}
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h4 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      margin: '0',
                      fontFamily: 'var(--font-family)'
                    }}>
                      {card.title}
                    </h4>
                  </div>
                  <ArrowRight
                    size={20}
                    className="asset-card-arrow"
                  />
                </div>

                {/* Description */}
                <p style={{
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                  margin: '0 0 12px 0',
                  lineHeight: '1.4',
                  fontFamily: 'var(--font-family)'
                }}>
                  {card.description}
                </p>

                {/* Features */}
                <div>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Key Features
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    {card.features.map((feature, index) => (
                      <span
                        key={index}
                        style={{
                          fontSize: '12px',
                          padding: '4px 8px',
                          background: '#f3f4f6',
                          color: '#374151',
                          borderRadius: '6px',
                          fontWeight: '500',
                        }}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            ))
          ) : (
            <div style={{ 
              gridColumn: '1 / -1', 
              padding: '60px 20px', 
              textAlign: 'center',
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '1px dashed #D1D5DB'
            }}>
              <Search size={32} style={{ color: '#9CA3AF', marginBottom: '16px' }} />
              <h3 style={{ fontSize: '16px', fontWeight: '500', color: '#374151', margin: '0 0 8px 0' }}>
                No results found
              </h3>
              <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                We couldn't find any finance modules matching "{searchQuery}"
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FinanceLanding;